import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle2, XCircle, TrendingDown, ArrowRight } from 'lucide-react';
const faqs = [{
  question: 'Who is this for?',
  answer: 'This is for you if you feel anxious "for no reason," overthink constantly, your body stays tense even when life is fine, you struggle to sleep because your mind won\'t stop, and you want practical steps — not long lectures. Especially if you prefer: clarity, structure, and simplicity.'
}, {
  question: 'Who is this NOT for?',
  answer: 'This is not for you if you want a magical 5-minute cure, endless theory, "just be positive" advice, medication guidance, or spiritual bypassing. This is regulation. Applied daily. Small actions. Real change.'
}, {
  question: 'Is this for me if I take medication?',
  answer: 'Yes. This guide focuses on behavioral and physiological regulation tools. It complements medication — it does not replace it. Always consult your doctor regarding your treatment plan.'
}, {
  question: 'How much time does it take daily?',
  answer: 'About 15-20 minutes. Each day includes a 3-minute fallback option for busy days. The actions are designed to be small and manageable — not another overwhelming task.'
}, {
  question: 'Is this "positive thinking" or therapy?',
  answer: "Neither. This is a nervous system regulation plan. It focuses on physical signals (breathing, attention, tension) rather than analyzing your thoughts or past. It's body-first, not mind-first."
}, {
  question: 'What results can I expect?',
  answer: 'After 7 days, most readers report: lower resting anxiety, fewer thought spirals, better sleep quality, less physical tension, and faster recovery after stress. Not "zero anxiety" — lower baseline. More control. More peace.'
}, {
  question: 'What if anxiety comes back?',
  answer: "The guide includes a Relapse Plan (exact steps for when anxiety spikes) and a Weekly Reset Protocol (15-25 minutes). You'll know exactly what to do."
}, {
  question: 'What if I fall behind?',
  answer: 'That\'s okay. The guide is yours forever. You can restart, pause, or repeat days as needed. There is no "failing" here.'
}];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-20 bg-[#F5F5FA] relative overflow-hidden" data-id="element-132">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-133">
        {/* Who This Is For / Not For */}
        <div className="max-w-5xl mx-auto mb-16" data-id="element-134">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-135">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4" data-id="element-136">
              Is this{' '}
              <span className="relative inline-block" data-id="element-137">
                <span className="relative z-10" data-id="element-138">right for you?</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-[#FDE68A]/60 -z-0 rounded-sm" data-id="element-139"></span>
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-140">
            {/* For You */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl p-6 border border-emerald-200 shadow-sm" data-id="element-141">
              <h3 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2" data-id="element-142">
                <CheckCircle2 className="w-5 h-5" data-id="element-143" />
                This IS for you if:
              </h3>
              <ul className="space-y-3" data-id="element-144">
                {['You feel anxious "for no reason"', 'You overthink constantly', 'Your body stays tense even when life is fine', "You struggle to sleep — your mind won't stop", 'You want practical steps, not long lectures', 'You prefer clarity, structure, simplicity'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#44403C]" data-id="element-145">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-146" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Not For You */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl p-6 border border-rose-200 shadow-sm" data-id="element-147">
              <h3 className="text-lg font-bold text-rose-700 mb-4 flex items-center gap-2" data-id="element-148">
                <XCircle className="w-5 h-5" data-id="element-149" />
                This is NOT for you if:
              </h3>
              <ul className="space-y-3" data-id="element-150">
                {['You want a magical 5-minute cure', 'You prefer endless theory over action', 'You want "just be positive" advice', 'You need medication guidance', "You're looking for spiritual bypassing"].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#44403C]" data-id="element-151">
                    <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" data-id="element-152" />
                    {item}
                  </li>)}
              </ul>
              <p className="mt-4 text-xs text-[#78716C] italic" data-id="element-153">
                This is regulation. Applied daily. Small actions. Real change.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Cost of Doing Nothing */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto mb-16" data-id="element-154">
          <div className="bg-[#1E1B4B] rounded-2xl p-6 md:p-8 text-white" data-id="element-155">
            <div className="flex items-center gap-2 mb-4" data-id="element-156">
              <TrendingDown className="w-5 h-5 text-[#FCA5A5]" data-id="element-157" />
              <h3 className="text-lg font-bold" data-id="element-158">The cost of doing nothing</h3>
            </div>
            <p className="text-[#C7D2FE] mb-4 leading-relaxed" data-id="element-159">
              If anxiety continues unchecked, it usually doesn't "go away." It
              becomes your{' '}
              <span className="text-white font-semibold" data-id="element-160">default setting.</span>
            </p>
            <ul className="space-y-2 mb-4" data-id="element-161">
              {['Sleep gets lighter', 'Avoidance grows', 'Your tolerance shrinks', 'Small stress feels big', 'You spend more energy "managing yourself" than living'].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm text-[#E0E7FF]" data-id="element-162">
                  <ArrowRight className="w-3 h-3 text-[#FCA5A5] flex-shrink-0" data-id="element-163" />
                  {item}
                </li>)}
            </ul>
            <p className="text-[#FDE68A] font-bold text-sm" data-id="element-164">
              Waiting doesn't reduce anxiety. Training does.
            </p>
          </div>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto" data-id="element-165">
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-166">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4" data-id="element-167">
              Common Questions
            </h2>
            <p className="text-[#44403C]" data-id="element-168">Clarity before you start.</p>
          </motion.div>

          <div className="space-y-4" data-id="element-169">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className={`rounded-xl overflow-hidden shadow-sm transition-all duration-300 border ${openIndex === index ? 'bg-white border-[#4338CA] ring-1 ring-[#4338CA]' : 'bg-white border-[#E0E7FF] hover:border-[#4338CA]/50'}`} data-id="element-170">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-171">
                  <span className="font-bold text-[#1E1B4B] text-lg" data-id="element-172">
                    {faq.question}
                  </span>
                  <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${openIndex === index ? 'rotate-180 text-[#4338CA]' : 'text-[#78716C]'}`} data-id="element-173">
                    <ChevronDown className="w-5 h-5" data-id="element-174" />
                  </div>
                </button>
                <AnimatePresence data-id="element-175">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-176">
                      <div className="px-6 pb-6 text-[#44403C] leading-relaxed" data-id="element-177">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}